import React, { useEffect, useState } from 'react';
import { Button, Table, Text, Title, ListingImage } from 'components';
import { startCase } from 'lodash';
import styled from 'styled-components';
import { TablePaginationConfig } from 'antd/es/table';
import { OwnerAndAssigneeType } from '@bb/domain/dist/types/SupraLockbox';
import { PopulatedUser } from 'api/UserController/getProfile';
import { CachedOrg } from '@bb/automaton';
import { Modal } from 'antd';
import type { JsonObject, SupraLockboxJsonType } from '@/app-package';
import { useHistory } from 'react-router-dom';
import { buildLockboxAssignConfirmMessage } from 'utils';

type LockboxTableProps = {
  lockboxData: SupraLockboxJsonType[];
  totalLockboxCount: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  unassignLockbox: (serialNumber: string, userId: string) => Promise<void>;
  isLimitedWithAssignLockboxDisabled: boolean;
  canAssignLockboxesToUser: boolean;
  loadingData: boolean;
  user: JsonObject<PopulatedUser & { org: CachedOrg }> | null;
};

const LockboxTable = ({
  lockboxData,
  page,
  setPage,
  totalLockboxCount,
  unassignLockbox,
  isLimitedWithAssignLockboxDisabled,
  canAssignLockboxesToUser,
  loadingData,
  user,
}: LockboxTableProps) => {
  useEffect(() => {
    if (lockboxData.length === 0) {
      setPage(1);
    }
  }, [lockboxData, setPage]);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [unassignData, setUnassignData] = useState<{
    serialNumber: string;
    listingText: string;
    isAssignee: boolean;
    ownerType: string;
    assigneeType: string;
  } | null>(null);

  const history = useHistory();

  const handleUnassignClick = (
    serialNumber: string,
    listingText: string,
    assigneeId: string,
    ownerType: string,
    assigneeType: string,
  ) => {
    setUnassignData({
      serialNumber,
      listingText,
      isAssignee: Boolean(user?.id) && assigneeId === user!.id,
      ownerType,
      assigneeType,
    });
    setConfirmVisible(true);
  };

  const handleConfirmOk = () => {
    if (unassignData && user?.id) {
      unassignLockbox(unassignData.serialNumber, user.id);
    }
    setConfirmVisible(false);
  };

  const handleConfirmCancel = () => {
    setConfirmVisible(false);
  };

  const getOwnerOrAssigneeLabel = (
    type: string,
    mlsOwnerName?: string,
    branchIndex?: string,
  ) => {
    switch (type) {
      case 'branch':
        return branchIndex
          ? user?.org.locations[parseInt(branchIndex, 10)].name
          : startCase(type);
      case 'org':
        return user?.org.name;
      case 'mls':
        return mlsOwnerName || 'MLS';
      default:
        return startCase(type);
    }
  };

  const columns = [
    {
      title: 'LOCKBOX',
      dataIndex: 'productCode',
      key: 'productCode',
      render: (type: string) => type === 'iBox' ? 'iBox BT LE' : 'Invoy',
    },
    { title: 'SERIAL #', dataIndex: 'serialNumber', key: 'serialNumber' },
    {
      title: 'LISTING',
      dataIndex: 'listing',
      key: 'listing',
      render: (_: any, { listing }: any) =>
        listing ? (
          <StyledListing>
            <ListingImage
              imageSrc={listing.thumbnail}
              size={24}
              fixProportion
            />{' '}
            <StyledListingText>
              {listing.line1} {listing.line2}
            </StyledListingText>
          </StyledListing>
        ) : (
          <StyledUnassingedText type="secondary">
            Unassigned
          </StyledUnassingedText>
        ),
    },
    {
      title: 'OWNER',
      dataIndex: 'owner',
      key: 'owner',
      render: (
        owner: { _id: string; firstName: string; lastName: string },
        {
          ownerType,
          ownerBranchIndex,
          mlsOwnerName,
        }: {
          ownerType: string;
          ownerBranchIndex?: string;
          mlsOwnerName?: string;
        },
      ) =>
        owner?.firstName
          ? `${owner.firstName} ${owner.lastName}`
          : getOwnerOrAssigneeLabel(ownerType, mlsOwnerName, ownerBranchIndex),
    },
    {
      title: 'ASSIGNEE',
      dataIndex: 'assignee',
      key: 'assignee',
      render: (
        assignee: { _id: string; firstName: string; lastName: string },
        {
          assigneeType,
          assigneeBranchIndex,
        }: { assigneeType?: string; assigneeBranchIndex?: string },
      ) => {
        if (!assigneeType) {
          return (
            <StyledUnassingedText type="secondary">
              Unassigned
            </StyledUnassingedText>
          );
        }
        return assignee?.firstName
          ? `${assignee.firstName} ${assignee.lastName}`
          : assigneeType &&
              getOwnerOrAssigneeLabel(
                assigneeType,
                undefined,
                assigneeBranchIndex,
              );
      },
    },

    {
      title: '',
      dataIndex: 'unassign',
      key: 'unassign',
      render: (
        _: any,
        {
          serialNumber,
          listing,
          assignee,
          ownerType,
          assigneeType,
          mlsStaffHasPermission,
        }: any,
      ) => {
        if (!mlsStaffHasPermission) return null;
        return (
          <Button
            type="link"
            onClick={e => {
              e.stopPropagation();
              handleUnassignClick(
                serialNumber,
                listing?.line1
                  ? `${listing.line1} ${listing?.line2 ?? ''}`.trim()
                  : '',
                assignee?._id ?? '',
                ownerType,
                assigneeType,
              );
            }}
          >
            Unassign
          </Button>
        );
      },
    },
  ];

  const onChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setPage(pagination.current);
    }
  };
  const getEmptyDataComponent = () => ({
      emptyText: (
        <StyledEmptyDataContainer>
          <Title level={3}>
            No lockboxes {isLimitedWithAssignLockboxDisabled ? '' : 'yet'}
          </Title>
          {isLimitedWithAssignLockboxDisabled ? (
            <Text type="secondary">User cannot be assigned lockboxes</Text>
          ) : canAssignLockboxesToUser ? (
            <Text type="secondary">
              Click Add Lockbox to assign user a lockbox
            </Text>
          ) : (
            <>
              <Text type="secondary">
                This user can&apos;t be assigned a lockbox without
              </Text>
              <Text type="secondary">having an supra subscription</Text>
            </>
          )}
        </StyledEmptyDataContainer>
      ),
    });

  return (
    <>
      <StyledTable
        loading={loadingData}
        locale={getEmptyDataComponent()}
        pagination={{ pageSize: 5, total: totalLockboxCount, current: page }}
        dataSource={lockboxData}
        columns={columns}
        onChange={onChange}
        onRow={lockbox => ({
          onClick: () => {
            if (!lockbox.mlsStaffHasPermission) return;
            history.push(`/mls/lockbox/${lockbox._id}`);
          },
          style: { cursor: 'pointer' },
          'data-cy': `lockbox-row-${lockbox._id}`,
        })}
        rowKey="serialNumber"
      />
      <Modal
        title="Confirm Unassign"
        open={confirmVisible}
        onCancel={handleConfirmCancel}
        footer={[
          <Button key="cancel" onClick={handleConfirmCancel}>
            Cancel
          </Button>,
          <Button key="unassign" type="primary" onClick={handleConfirmOk}>
            Unassign
          </Button>,
        ]}
      >
        <p>
          {buildLockboxAssignConfirmMessage({
            action: 'Unassigning',
            ownerType: unassignData?.ownerType as OwnerAndAssigneeType,
            assigneeType: unassignData?.assigneeType as OwnerAndAssigneeType,
            isAssignee: !!unassignData?.isAssignee,
            listingAddress: unassignData?.listingText,
          })}
        </p>
      </Modal>
    </>
  );
};

export default LockboxTable;

const StyledTable = styled(Table)`
  .ant-pagination {
    justify-content: center;
  }
`;

const StyledEmptyDataContainer = styled.div`
  margin: 20px 0px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledListingText = styled(Text)`
  margin-left: 5px;
`;

const StyledUnassingedText = styled(Text)`
  word-break: normal;
`;

const StyledListing = styled.div`
  display: flex;
  align-items: center;
`;
